// @flow
/** @jsx jsx */

import * as React from 'react';
import { darken } from 'polished';

import Icon, {
  type IconNameType,
} from '@zapier/common-components/src/components/Icon';
import { type ColorName, Colors, Border } from '@zapier/theme';
import { css, jsx } from '@emotion/core';
import { keyCodes, marginToCss, paddingToCss } from '@zapier/common-utils';

import type { Spacing } from '@zapier/common-utils';

const height = 20;
const letterSpacing = 0.15;
const defaultPadding = {
  vertical: '0',
  horizontal: '9px',
};

type Props = {
  /** The name of the Zapier Visual Identity color to render as the background color. */
  backgroundColor: ColorName,
  /** Children to render. Typically text nodes. Accepts a Node to support minor variations. Does not support block elements. */
  children: React.Node,
  /** Optional font weight you wish to apply to the component. */
  fontWeight?: number,
  /** The name of the Zapier Visual Identity color to render as the icon color. */
  iconColor?: ColorName,
  /** The type of icon to render (see Icon/icons/ folder for all available icons. If not provided, no icon is rendered. */
  iconType?: IconNameType,
  /** Show borders around tag */
  hasBorder?: boolean,
  /** If true, renders the tag in a pill shape. */
  isPill: boolean,
  /** The font size to render the text value */
  fontSize: number,
  /** The name of the Zapier Visual Identity color to render as the text color. */
  textColor: ColorName,
  /** Called if the tag is clicked. If provided, the tag becomes focusable, clickable, and keyboard accessible. */
  onClick?: () => {},
  /** HTML tabindex attribute. Usually only used if onClick is provided. */
  tabIndex: number,
  /** Optional margin value(s) you wish to apply to the component. */
  margin?: Spacing,
  /** Optional padding value(s) you wish to apply to the component. */
  padding: Spacing,
};

const tagBaseCss = ({ margin, padding, fontWeight, fontSize }: Props) => ({
  ...marginToCss(margin),
  ...paddingToCss(padding),
  alignItems: 'center',
  background: Colors.steel,
  borderRadius: 3,
  color: 'white',
  display: 'inline-flex',
  fontSize,
  fontWeight,
  height,
  justifyContent: 'center',
  letterSpacing,
  lineHeight: `${height}px`,
  overflow: 'hidden',
  textAlign: 'center',
  textIndent: letterSpacing, // Offset end of line space from `letter-spacing`.
  textTransform: 'uppercase',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
});

const tagBorderCss = backgroundColor => ({
  border: `${Border.size.default} solid ${darken(0.2, backgroundColor)}`,
});

const tagClickableCss = {
  cursor: 'pointer',
};

const tagPillCss = ({ padding }) => ({
  borderRadius: 50,
  paddingLeft: padding.horizontal ? padding.horizontal : 6,
  paddingRight: padding.horizontal ? padding.horizontal : 6,
});

/**
 * Renders a tag.
 */
const Tag = (props: Props) => {
  const { children, iconType, tabIndex } = props;
  const backgroundColor = Colors[props.backgroundColor];

  const tagCss = css(
    tagBaseCss(props),
    props.hasBorder && tagBorderCss(backgroundColor),
    props.onClick && tagClickableCss,
    props.isPill && tagPillCss(props),
    {
      background: backgroundColor,
      color: Colors[props.textColor],
    }
  );

  // Prevents event object from leaking to consumers of this component,
  // which would couple consumers to this component's implementation.
  const onClick = () => props.onClick && props.onClick();
  const iconColor = props.iconColor
    ? Colors[props.iconColor]
    : Colors[props.textColor];
  const iconSize = props.fontSize * 1.4;
  const icon = iconType && (
    <React.Fragment>
      <Icon color={iconColor} name={iconType} size={iconSize} />
      &nbsp;
    </React.Fragment>
  );

  // Elements with role="button" should be able to receive focus, and be
  // triggered by pressing Space or Enter. role="button" is used for this element
  // because conditionally rendering a <button> presents styling challenges.
  // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_button_role#Keyboard_and_focus
  const clickableProps = props.onClick && {
    role: 'button',
    onClick,
    tabIndex,
    onKeyPress: (e: SyntheticKeyboardEvent<>) => {
      const {
        // "Property keyCode is missing in Event [1]." No it ain't!
        // $FlowFixMe
        nativeEvent: { keyCode },
      } = e;

      if (keyCode === keyCodes.Enter || keyCode === keyCodes.Space) {
        onClick();
      }
    },
  };

  return (
    <div css={tagCss} {...clickableProps}>
      {icon}
      {children}
    </div>
  );
};

Tag.defaultProps = {
  backgroundColor: 'steel',
  fontWeight: 800,
  hasBorder: false,
  isPill: false,
  padding: defaultPadding,
  fontSize: 10,
  tabIndex: 0,
  textColor: 'white',
};

export default Tag;
